<template>
    <div class="w-full">
        <SessionExpiredBanner
            class="sticky top-0 z-[9999999]"
            @sign-in="openSignIn"
            @retry="retrySave"
        ></SessionExpiredBanner>
        <div
            v-if="loading"
            class="mt-96 flex w-full items-start justify-center"
        >
            <LoadingSpinner size="large" :delay="false"></LoadingSpinner>
        </div>
        <TopolEditor
            v-show="!loading"
            :options="topolOptions"
            :stage="props.stage"
            @onSave="callbacks.onSave"
            @onSaveAndClose="callbacks.onSaveAndClose"
            @onTestSend="callbacks.onTestSend"
            @onInit="callbacks.onInit"
            @onBlockSave="callbacks.onBlockSave"
            @onBlockRemove="callbacks.onBlockRemove"
            @onBlockEdit="callbacks.onBlockEdit"
            @onAlert="callbacks.onAlert"
            @onClose="callbacks.onClose"
            @onLoaded="callbacks.onLoaded"
            @onTemplateRename="callbacks.onRenameTemplate"
            @updateTestingEmailAddresses="callbacks.updateTestingEmailAddresses"
        ></TopolEditor>
    </div>
</template>

<script lang="ts" setup>
import {
    TopolEditor,
    TopolPlugin,
    ITopolOptions,
    ISavedBlock,
    INotification,
    IStage,
} from '@topol.io/editor-vue';

import LoadingSpinner from '@components/Atomic/LoadingSpinner/LoadingSpinner.vue';
import SessionExpiredBanner from './components/SessionExpiredBanner.vue';

import { ref } from 'vue';
import { router } from '@inertiajs/vue3';
import axios from 'axios';

import useSavedBlocksService from './composables/useSavedBlocksService';
import useSendTestService from './composables/useSendTestService';
import { create as createNotification } from '@composables/useNotifications';
import useEditorSession, {
    EDITOR_REQUEST_HEADERS,
    markSaved,
} from '@composables/useEditorSession';

import useConfirm from '@composables/useConfirm';
import useFonts from '@/Pages/EditorPreferences/Fonts/composables/useFonts';
import useEditorTranslations from './composables/useEditorTranslations';
import { ITopolPageProps } from '@/types/TopolPageProps/TopolPageProps';
import ICurrentTeam from '@/types/Members/ICurrentTeam';
import usePusher from '@/composables/usePusher';
import useCookiebot from '@/composables/useCookiebot';
import { useGtm } from '@/composables/useGtm';
import { useSubscriptionLabel } from '@/composables/useSubscriptionLabel';

const { startEcho, stopEcho } = usePusher();
const { show } = useConfirm();

type CurrentUser = {
    id: number;
    name: string;
    profile_photo_path: string;
    profile_photo_url: string;
};

interface Props {
    apiKey: string;
    token: string;
    definition: string;
    saveUrl: string;
    pingUrl: string;
    backUrl: string;
    savedBlocks: ISavedBlock[];
    saveSavedBlockUrl: string;
    getAutosaveUrl: string;
    getConversationsUrl: string;
    getCommentsUrl: string;
    sendTestEmailUrl: string;
    createAutosaveUrl: string;
    getAutosavesUrl: string;
    theme: string;
    fontSizes: string;
    defaultFonts: string;
    customFonts: string;
    smartMergeTags: string;
    mergeTags: string;
    currentUser: CurrentUser;
    ownerId: string | number;
    templateId: number;
    language?: string;
    colors: string;
    title?: string;
    stage?: IStage;
    enableMergeTagPreview?: boolean;
    imageCompressionOptions: string;
    enableImageToTextRatio: boolean;
    chatAI: boolean;
    googlePromotionalAnnotation: boolean;
    lambdaStage: 'production' | 'develop';
    userRole: string;
    getFeedsUrl: string;
    getProductsUrl: string;
    templateStatus: string | null;
    enableComments?: boolean;
    renameTemplate?: boolean;
    htmlMinified: boolean;
    enableSectionVariants: boolean;
    emailServiceProvider?: 'mailchimp'|'sparkpost';
    isSubscribed: boolean;
    testingEmails: string;
    teamUsers: string;
    multilingual: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    language: 'en',
    title: '',
    stage: 'production',
    enableComments: true,
    renameTemplate: true,
});

const globalProps = computed(() => usePage<ITopolPageProps>().props);
const teamProps = computed(() => globalProps.value.team as ICurrentTeam);
const { hideCookieBot, showCookieBot } = useCookiebot();
const { pushEvent } = useGtm();
const { planName } = useSubscriptionLabel();

onMounted(() => {
    hideCookieBot();

    listenForComments();

    heartbeatTimer = setInterval(() => ping(), HEARTBEAT_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisibilityChange);
});

onUnmounted(() => {
    showCookieBot();

    stopListeningForComments();

    if (heartbeatTimer !== undefined) {
        clearInterval(heartbeatTimer);
    }
    document.removeEventListener('visibilitychange', onVisibilityChange);
});

const listenForComments = () => {
    if (teamProps.value.personal_team === false) {
        startEcho();

        window.Echo.private(
            'template-conversations.' + props.templateId
        ).listen(
            '.App\\Topol\\Templates\\Events\\TemplateConversationUpdated',
            (e) => {
                if (e.comment_by !== props.currentUser.id) {
                    window.TopolPlugin.refreshComments(e.key);
                }
            }
        );
    }
};

const stopListeningForComments = () => {
    stopEcho();
};

const { defaultFonts: allDefaultFonts } = useFonts();
const loading = ref(true);

const {
    createSavedBlock,
    deleteSavedBlock,
    editSavedBlock,
    prepareSavedBlocks,
} = useSavedBlocksService(
    props.savedBlocks,
    props.saveSavedBlockUrl,
    props.language ?? 'en'
);

const { sendTestEmail } = useSendTestService(props.sendTestEmailUrl);

const { translations: editorTranslations, getTranslations } =
    useEditorTranslations(props.language ?? 'en');

// @ts-ignore
const translations = ref(editorTranslations[props.language ?? 'en']);

const { ping, isRetrying } = useEditorSession(props.pingUrl);

const HEARTBEAT_INTERVAL_MS = 60_000;
let heartbeatTimer: ReturnType<typeof setInterval> | undefined;

const onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
        ping();
    }
};

const saveDefinition = (json: unknown, html: unknown) =>
    axios.post(
        props.saveUrl,
        {
            definition: JSON.stringify(json),
            html: html,
        },
        { headers: EDITOR_REQUEST_HEADERS }
    );

const openSignIn = (): void => {
    window.open(route('login'), '_blank');
};

const retrySave = async (): Promise<void> => {
    if (isRetrying.value) {
        return;
    }

    isRetrying.value = true;

    const stillValid = await ping();

    if (!stillValid) {
        createNotification({
            title: 'Still signed out — sign in first.',
            type: 'error',
        });

        isRetrying.value = false;

        return;
    }

    // Re-fire onSave with the CURRENT editor content rather than replaying a
    // stale snapshot captured at the original failed save. isRetrying stays set
    // until onSave settles the re-fired save (see its `finally`), so a second
    // click can't trigger a duplicate concurrent save.
    TopolPlugin.save();
};

const getEditorRole = () => {
    switch (props.userRole) {
        case 'owner':
        case 'admin':
            return 'manager';
        case 'editor':
            return 'editor';
        case 'viewer':
            return 'reader';
        default:
            return 'reader';
    }
};

const callbacks = {
    async onSave({ json, html }: { json: unknown; html: unknown }) {
        try {
            await saveDefinition(json, html);

            markSaved();

            createNotification({
                title: translations.value.saveSuccess,
                type: 'success',
            });
        } catch (e) {
            // A 401/419 has already raised the session banner via the axios
            // interceptor; any other error is toasted there too. Either way we
            // must not show a false "Saved!" message.
        } finally {
            // Release the retry guard once the (possibly re-fired) save settles.
            isRetrying.value = false;
        }
    },

    async updateTestingEmailAddresses(emails: string[]) {
        await axios.put(
            route('editor-preferences.testing-emails.update'),
            {
                emails: emails,
            },
            { headers: EDITOR_REQUEST_HEADERS }
        );
    },

    async onSaveAndClose({ json, html }: { json: unknown; html: unknown }) {
        try {
            await saveDefinition(json, html);

            markSaved();

            router.visit(props.backUrl);
        } catch (e) {
            // Session expired or the save failed - stay in the editor so the
            // banner can warn the user instead of navigating away from unsaved
            // work.
        }
    },

    async onBlockSave(json: ISavedBlock) {
        const newSavedBlocks = await createSavedBlock(json);

        if (newSavedBlocks !== undefined) {
            TopolPlugin.setSavedBlocks(newSavedBlocks);

            createNotification({
                title: translations.value.saveBlockSuccess,
                text: ``,
                type: 'success',
            });
        }
    },

    async onBlockRemove(templateSavedBlockId: number) {
        const newSavedBlocks = await deleteSavedBlock(templateSavedBlockId);

        if (newSavedBlocks !== undefined) {
            TopolPlugin.setSavedBlocks(newSavedBlocks);

            createNotification({
                title: translations.value.removeBlockSuccess,
                text: ``,
                type: 'success',
            });
        }
    },

    async onBlockEdit(templateSavedBlockId: number) {
        const newSavedBlocks = await editSavedBlock(templateSavedBlockId);

        if (newSavedBlocks !== undefined) {
            TopolPlugin.setSavedBlocks(newSavedBlocks);

            createNotification({
                title: translations.value.updateBlockSuccess,
                text: ``,
                type: 'success',
            });
        }
    },

    async onTestSend({ email, html }: { email: string[]; html: unknown }) {
        if (!props.isSubscribed) {
            createNotification({
                title: 'Email not sent',
                text: `You need to be a paid user to send test emails. Please select an appropriate ${planName.value} subscription to continue.`,
                type: 'error',
            });

            return;
        }

        try {
            const res = await sendTestEmail(email, html);

            if (res.data.success === true) {
                createNotification({
                    title: translations.value.sentEmailSuccessTitle,
                    text: getTranslations('sentEmailSuccess', email),
                    type: 'success',
                });
                pushEvent('topolpro_send_test_link');
            } else {
                createNotification({
                    title: translations.value.sentEmailFailedTitle,
                    text: getTranslations('sentEmailFailed', email),
                    type: 'error',
                });
            }
        } catch (err) {
            createNotification({
                title: translations.value.sentEmailFailedTitle,
                text: getTranslations('sentEmailFailed', email),
                type: 'error',
            });
        }
    },

    onAlert(notification: INotification) {
        if (!notification.expectSideEffect) {
            createNotification({
                title: notification.title,
                text: notification.text,
                type: notification.type,
                persistent: notification.persistent,
            });
        }
    },

    onInit() {
        TopolPlugin.load(props.definition);
    },

    onLoaded() {
        loading.value = false;
    },

    async onClose() {
        const confirmed = await show({
            type: 'danger',
            header: translations.value.closeEditorTitle,
            message: translations.value.closeEditorText,
            confirm: translations.value.closeEditorConfirm,
            cancel: translations.value.cancel,
        });

        if (confirmed) {
            router.visit(props.backUrl);
        }
    },
    async onRenameTemplate() {
        const newTemplateName = await show({
            header: translations.value.templateRenameTitle,
            hasInput: true,
            inputData: {
                label: translations.value.templateRenameLabel,
                defaultValue: props.title,
            },
            confirm: translations.value.rename,
            cancel: translations.value.cancel,
        });

        if (typeof newTemplateName === 'string') {
            try {
                const result = await axios.put(
                    route('templates.update', {
                        template: props.templateId,
                    }),
                    {
                        name: newTemplateName,
                        status: props.templateStatus,
                    },
                    { headers: EDITOR_REQUEST_HEADERS }
                );
                if (result.data.success) {
                    createNotification({
                        title: translations.value.templateRenameSuccessTitle,
                        type: 'success',
                    });

                    window.TopolPlugin.setTemplateName(newTemplateName);
                    router.reload({ only: ['title'] });
                } else {
                    createNotification({
                        title: translations.value.templateRenameFailedTitle,
                        type: 'error',
                    });
                }
            } catch (e) {
                return;
            }
        }
    },
};

const topolOptions: ITopolOptions = {
    apiAuthorizationHeader: 'Bearer ' + props.token,

    title: props.title,

    authorize: {
        apiKey: props.apiKey,
        userId: props.ownerId,
    },

    currentUser: {
        userId: props.currentUser.id,
        name: props.currentUser.name,
        profilePhotoUrl:
            props.currentUser.profile_photo_path ??
            props.currentUser.profile_photo_url,
    },

    role: getEditorRole(),

    enableAutosaves: true,

    savedBlocks: prepareSavedBlocks(props.savedBlocks),

    api: {
        AUTOSAVES: props.getAutosavesUrl,
        GET_AUTOSAVE: props.getAutosaveUrl,
        AUTOSAVE: props.createAutosaveUrl,
        CONVERSATIONS: props.getConversationsUrl,
        COMMENTS: props.getCommentsUrl,
        FEEDS: props.getFeedsUrl,
        PRODUCTS: props.getProductsUrl,
    },

    windowBar: ['close', 'fullscreen'],

    smartMergeTags: JSON.parse(props.smartMergeTags),
    mergeTags: JSON.parse(props.mergeTags),

    mobileFirstEnabled: true,

    //we handle alerts on our own, to be able reflect our server request responses statuses
    disableAlerts: true,

    language: props.language,
    fontSizes: JSON.parse(props.fontSizes),

    theme: {
        preset: props.theme === 'light' ? 'light' : 'dark',
        colors: {
            active: '#FFC000',
            'active-light': '#FFC000',
        },
    },
    colors: JSON.parse(props.colors),

    betaFeatures: {
        carousel: true,
    },

    customFonts:
        props.customFonts === '[]' && props.defaultFonts === '[]'
            ? {
                  override: false,
                  fonts: [],
              }
            : {
                  override: true,
                  fonts:
                      props.defaultFonts === '[]'
                          ? allDefaultFonts.concat(
                                JSON.parse(props.customFonts)
                            )
                          : JSON.parse(props.defaultFonts).concat(
                                JSON.parse(props.customFonts)
                            ),
              },

    enableMergeTagPreview: props.enableMergeTagPreview ?? false,
    imageCompressionOptions: JSON.parse(props.imageCompressionOptions),
    enableImageToTextRatio: props.enableImageToTextRatio,
    chatAI: props.chatAI,
    googlePromotionalAnnotation: props.googlePromotionalAnnotation,
    lambdaStage: props.lambdaStage,
    stage: props.stage,
    enableComments: props.enableComments,
    renameTemplate: props.renameTemplate,
    htmlMinified: props.htmlMinified,
    enableSectionVariants: props.enableSectionVariants,
    emailServiceProvider: props.emailServiceProvider,
    imageEditorV2: true,
    testingEmails: props.testingEmails ? JSON.parse(props.testingEmails) : [],
    teamUsers: JSON.parse(props.teamUsers),
    multilingual: props.multilingual,
    enableDarkMode: true,
};
</script>
